import { DeliveryMethod } from '@/domain/models/Order/deliveryMethod';
import { OrderMethod } from '@/domain/models/Order/orderMethod';
import {
  ConnectDeliveryService,
  IntegrationStatusNum,
} from '@/pages/store-management/master-stores/service';
import { LinkedStatus } from '@/services/StoreManagement/MasterStores/hooks/show/useStoreManagementMasterStoreShow';

export type ExternalAuthDeliveryStore = {
  id: number;
  email: string;
  password: string;
  username: string;
};

export type EatInReceiveMethod = 'TABLE_DELIVERY' | 'COUNTER_PICKUP';

export type EatInSettings = {
  receive_methods: EatInReceiveMethod[];
  table_no_placeholder: string | null;
};

export type CamelOrderProp = {
  takeout_start_days_offset: number | null;
  takeout_end_days_offset: number | null;
  order_methods: OrderMethod[];
  pickup_notes: string | null;
  delivery_methods: DeliveryMethod[];
  is_asap_displayed: boolean;
  eat_in_settings?: EatInSettings | null;
};

export type CamelOrderPropField = keyof CamelOrderProp;
export type CamelOrderPropValue<T extends CamelOrderPropField> =
  CamelOrderProp[T];

export type InitialDeliveryStore = {
  id: string;
  external_store_id: string;
  delivery_service_name: ConnectDeliveryService | '';
  linked_status: LinkedStatus | '';
  integration_date: Date;
  integration_status: IntegrationStatusNum;
  external_auth_delivery_store: ExternalAuthDeliveryStore | null;
  cooking_time: number | null;
  camel_order_prop: CamelOrderProp | null;
  external_store_name: string | null;
};

export const INITIAL_EXTERNAL_AUTH_DELIVERY_STORE: ExternalAuthDeliveryStore = {
  id: 0,
  email: '',
  password: '',
  username: '',
};

export const UBER_DELIVERY_STORE: InitialDeliveryStore = {
  id: '',
  external_store_id: '',
  delivery_service_name: '',
  linked_status: '',
  integration_date: new Date(),
  integration_status: 0,
  external_auth_delivery_store: {
    id: 0,
    email: '',
    password: '',
    username: '',
  },
  cooking_time: null,
  camel_order_prop: null,
  external_store_name: null,
};

export const MENU_DELIVERY_STORE: InitialDeliveryStore = {
  id: '',
  external_store_id: '',
  delivery_service_name: '',
  linked_status: '',
  integration_date: new Date(),
  integration_status: 0,
  external_auth_delivery_store: {
    id: 0,
    email: '',
    password: '',
    username: '',
  },
  cooking_time: null,
  camel_order_prop: null,
  external_store_name: null,
};

export const DEMAECAN_DELIVERY_STORE: InitialDeliveryStore = {
  id: '',
  external_store_id: '',
  delivery_service_name: '',
  linked_status: '',
  integration_date: new Date(),
  integration_status: 0,
  external_auth_delivery_store: {
    id: 0,
    email: '',
    password: '',
    username: '',
  },
  cooking_time: null,
  camel_order_prop: null,
  external_store_name: null,
};

export const TAKEOUT_START_DAYS_OFFSET_DEFAULT = 0;
export const TAKEOUT_END_DAYS_OFFSET_DEFAULT = 30;
export const TAKEOUT_DAYS_OFFSET_MAX = 365;

export const CAMEL_ORDER_DELIVERY_STORE: InitialDeliveryStore = {
  id: '',
  external_store_id: '',
  delivery_service_name: '',
  linked_status: '',
  integration_date: new Date(),
  integration_status: 0,
  external_auth_delivery_store: {
    id: 0,
    email: '',
    password: '',
    username: '',
  },
  cooking_time: null,
  camel_order_prop: {
    takeout_start_days_offset: TAKEOUT_START_DAYS_OFFSET_DEFAULT,
    takeout_end_days_offset: TAKEOUT_END_DAYS_OFFSET_DEFAULT,
    order_methods: [],
    delivery_methods: [],
    pickup_notes: '',
    is_asap_displayed: true,
    eat_in_settings: null,
  },
  external_store_name: null,
};

export const CAMEL_CALL_DELIVERY_STORE: InitialDeliveryStore = {
  id: '',
  external_store_id: '',
  delivery_service_name: '',
  linked_status: '',
  integration_date: new Date(),
  integration_status: 0,
  external_auth_delivery_store: {
    id: 0,
    email: '',
    password: '',
    username: '',
  },
  cooking_time: null,
  camel_order_prop: {
    takeout_start_days_offset: TAKEOUT_START_DAYS_OFFSET_DEFAULT,
    takeout_end_days_offset: TAKEOUT_END_DAYS_OFFSET_DEFAULT,
    order_methods: [],
    delivery_methods: [],
    pickup_notes: '',
    is_asap_displayed: true,
  },
  external_store_name: null,
};

/**
 *
 * @ex consecutiveNums(5, 30, 5)
 * result: // 5,10,15,20,25,30
 */
const consecutiveNums = (start: number, max: number, num: number): number[] => {
  return Array.from(
    { length: Math.floor((max - start) / num) + 1 },
    (_, index) => start + index * num
  );
};

/**
 * @NOTE
 * COOKING_TIMES_BY_CAMEL_ORDER, COOKING_TIMES_BY_CAMEL_CALL を触る時は、camel-react-frontend の調理時間設定をで使っている COOKING_TIMES_BY_CAMEL_ORDER, COOKING_TIMES_BY_CAMEL_CALL を修正の必要があるため注意
 * ***/
const COOKING_TIMES_UNDER_THREE_HOURS_FOR_CAMEL_ORDER = consecutiveNums(
  5,
  180,
  5
);
const COOKING_TIME_EXTENSION_FOR_CAMEL_ORDER = [240, 300, 360]; // 180分超はこの値のみ許可

export const COOKING_TIMES_BY_CAMEL_ORDER = [
  ...COOKING_TIMES_UNDER_THREE_HOURS_FOR_CAMEL_ORDER,
  ...COOKING_TIME_EXTENSION_FOR_CAMEL_ORDER,
];

export const COOKING_TIMES_BY_CAMEL_CALL = consecutiveNums(5, 180, 5);
